import { gsap } from 'gsap'

export default class FinesseMarquee extends HTMLElement {
  private list: HTMLElement | null = null
  private items: HTMLElement[] = []
  private hoverItems: HTMLElement[] = []
  private prevButton: HTMLElement | null = null
  private nextButton: HTMLElement | null = null
  private tl: gsap.core.Timeline | null = null

  constructor() {
    super()
    this.list = this.querySelector('[data-list]')
    this.items = Array.from(this.querySelectorAll('[data-item]'))
    this.hoverItems = Array.from(this.querySelectorAll('[data-list-hover]'))
    
    if (this?.hoverItems.length > 0) {
      this?.hoverItems.forEach(hoverItem => {
        hoverItem.addEventListener('mouseover', this.handleHover.bind(this))
        hoverItem.addEventListener('mouseleave', this.handleLeave.bind(this))
      })
    }

    // buttons 
    this.prevButton = this.querySelector('[data-marquee-prev]')
    this.nextButton = this.querySelector('[data-marquee-next]')
    
    // previous
    this.prevButton?.addEventListener('click', this.handleClick.bind(this))
    this.prevButton?.addEventListener('mouseover', this.handleHover.bind(this))
    this.prevButton?.addEventListener('mouseleave', this.handleLeave.bind(this))

    // next
    this.nextButton?.addEventListener('click', this.handleClick.bind(this))
    this.nextButton?.addEventListener('mouseover', this.handleHover.bind(this))
    this.nextButton?.addEventListener('mouseleave', this.handleLeave.bind(this))

    this.className = 'relative block group'

    this.initMarquee()
  }

  private initMarquee() {
    const isMobile = window.innerWidth < 769

    if (this.list && this.items.length > 0) {
      this.tl = this.horizontalLoop(this.items, {
        repeat: -1,
        speed: isMobile ? 0.8 : 1.33,
        reversed: false,
        paddingRight: 0
      })
    }
  }

  handleClick(evt: MouseEvent) {
    const button = evt.currentTarget
    const index = this.tl?.current()

    if (button === this.prevButton) {
      if (index === 0) {
        this.tl?.previous({ duration: 0.25 })
      } else {
        this.tl?.previous()
      }
    } 
    
    if (button === this.nextButton) {
      this.tl?.next()
    }
  }

  handleHover() {
    console.log('hover')
    this.tl?.pause()
  }

  handleLeave() {
    console.log('leave')
    this.tl?.play()
  }

  private horizontalLoop(items: HTMLElement[], config: any) {
    items = gsap.utils.toArray(items);
    config = config || {};

    let tl = gsap.timeline({
      repeat: config.repeat,
      paused: config.paused,
      defaults: { ease: "none" },
      onReverseComplete: () => {
        tl.totalTime(tl.rawTime() + tl.duration() * 100);
      },
    }),
      length = items.length,
      startX = items[0].offsetLeft,
      times: number[] = [],
      widths: number[] = [],
      xPercents: number[] = [],
      curIndex = 0,
      pixelsPerSecond = (config.speed || 1) * 100,
      snap = config.snap === false ? (v: number) => v : gsap.utils.snap(config.snap || 1),
      totalWidth,
      curX,
      distanceToStart,
      distanceToLoop,
      item,
      i;

    gsap.set(items, {
      xPercent: (i, el) => {
        let w = widths[i] = el.offsetWidth;
        xPercents[i] = (parseFloat(gsap.getProperty(el, "x", "px") as string) / w) * 100 + Number(gsap.getProperty(el, "xPercent") as string);
        return xPercents[i];
      },
    });
    gsap.set(items, { x: 0 });
    totalWidth = items[length - 1].offsetLeft + (xPercents[length - 1] / 100) * widths[length - 1] - startX + items[length - 1].offsetWidth * Number(gsap.getProperty(items[length - 1], "scaleX")) + (parseFloat(config.paddingRight) || 0);

    for (i = 0; i < length; i++) {
      item = items[i];
      curX = xPercents[i] / 100 * widths[i];
      distanceToStart = item.offsetLeft + curX - startX;
      distanceToLoop = distanceToStart + widths[i];
      tl.to(item, {
        xPercent: snap(((curX - distanceToLoop) / widths[i] * 100)),
        duration: distanceToLoop / pixelsPerSecond,
      }, 0)
        .fromTo(item, {
          xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i] * 100)),
        }, {
          xPercent: xPercents[i],
          duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
          immediateRender: false,
        }, distanceToLoop / pixelsPerSecond)
        .add("label" + i, distanceToStart / pixelsPerSecond);
      times[i] = distanceToStart / pixelsPerSecond;
    }

    function toIndex(index: number, vars: gsap.TweenVars = { duration: 0.25, ease: 'expo.easeInOut' }) {
      vars = vars || {};
      Math.abs(index - curIndex) > length / 2 && (index += index > curIndex ? -length : length);
      let newIndex = gsap.utils.wrap(0, length, index),
        time = times[newIndex];
      if (time > tl.time() !== index > curIndex) {
        vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
        time += tl.duration() * (index > curIndex ? 1 : -1);
      }
      curIndex = newIndex;
      vars.overwrite = true;
      return tl.tweenTo(time, vars);
    }

    function snapToIndex(index: number, config: { duration: number }) {
      // Ensure index is within bounds
      const boundedIndex = gsap.utils.wrap(0, length, index);
      gsap.to(tl, { time: times[boundedIndex], ...config });
      curIndex = boundedIndex;
    }

    const tlWithMethods = tl as gsap.core.Timeline & {
      next: (vars?: gsap.TweenVars) => gsap.core.Tween;
      previous: (vars?: gsap.TweenVars) => gsap.core.Tween;
      current: () => number;
      toIndex: (index: number, vars?: gsap.TweenVars) => gsap.core.Tween;
      snapToIndex: (index: number, config?: { duration: number }) => void;
      times: number[];
    };

    tlWithMethods.next = (vars) => toIndex(curIndex + 1, vars);
    tlWithMethods.previous = (vars) => toIndex(curIndex - 1, vars);
    tlWithMethods.current = () => curIndex;
    tlWithMethods.toIndex = (index, vars) => toIndex(index, vars);
    tlWithMethods.snapToIndex = (index: number, config = { duration: 800 }) => snapToIndex(index, config);
    tlWithMethods.times = times;

    if (config.reversed) {
      tl.vars?.onReverseComplete?.();
      tl.reverse();
    }
  
    return tlWithMethods;
  }
}

customElements.define('finesse-marquee', FinesseMarquee)